/**
 * Section 03a — Performance Analytics.
 *
 * The /analytics page: KPI band + equity/expectancy/R-distribution/hold charts over
 * your CLOSED trades, with scope (All / Auto / Manual) and date-range toggles.
 */
import React from 'react';
import { Typography, List, ListItem, ListItemText } from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import GuideCallout from '../GuideCallout';
import OpenInApp from '../OpenInApp';
import { GuideSection } from './types';

const H = ({ id, children }: { id: string; children: React.ReactNode }) => (
  <Typography id={`anchor-${id}`} variant="h5" sx={{ fontWeight: 700, mt: 4, mb: 1.25, scrollMarginTop: 80 }}>{children}</Typography>
);
const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" sx={{ mb: 1.25, lineHeight: 1.65, fontSize: '0.95rem' }}>{children}</Typography>
);
const LI = ({ children }: { children: React.ReactNode }) => (
  <ListItem sx={{ display: 'list-item', listStyleType: 'disc', ml: 3 }}><ListItemText primary={children} /></ListItem>
);

const Section_analytics: GuideSection = {
  id: 'analytics',
  label: 'Analytics',
  group: 'pages',
  icon: <InsightsIcon fontSize="small" />,
  render: () => (
    <>
      <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Performance Analytics</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        Your trading scorecard, computed from your <strong>closed trades</strong> — at
        <OpenInApp to="/analytics" label="Analytics" /> (sits right under <strong>Trades</strong>, its review
        companion). Two toggles at the top: <strong>scope</strong> (All / Auto / Manual / Edge AI) and{' '}
        <strong>date range</strong> (7d / 30d / 90d / 180d / 1Y / All).
      </Typography>

      <H id="scope">Scope &amp; range</H>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>All</strong> — every closed trade.</></LI>
        <LI><><strong>Auto</strong> — signal/alert-driven trades the system placed (including autonomous
          RockBand). Populates as auto-trades close.</></LI>
        <LI><><strong>Manual</strong> — chart-initiated, hand-added, or broker-imported trades.</></LI>
        <LI><><strong>Edge AI</strong> — the real trades that <strong>Edge AI (Pragya) had flagged</strong>:
          matched to a same-symbol, same-direction pick just before your entry. This is how you validate the
          engine against <em>real money</em> — "when I actually traded Pragya's picks, did they pay off?"</></LI>
      </List>

      <H id="kpis">The KPI band</H>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Net P&L</strong> — total money made/lost over the closed trades in scope.</></LI>
        <LI><><strong>Win rate</strong> — share of trades that were profitable (wins / losses / scratches).</></LI>
        <LI><><strong>Expectancy (R)</strong> — expected R-multiple per trade = pWin·avgWinR + pLoss·avgLossR.
          The single most telling number; positive = a positive edge per trade.</></LI>
        <LI><><strong>Profit factor</strong> — Σ winning P&L ÷ |Σ losing P&L| (&gt;1 is profitable).</></LI>
        <LI><><strong>Avg R</strong> — mean R-multiple across closed trades.</></LI>
        <LI><><strong>Max DD</strong> — peak-to-trough drop in cumulative equity (₹ and % of peak).</></LI>
        <LI><><strong>Capture eff.</strong> — share of the favourable move (MFE) you actually captured;
          populates as MFE data accrues.</></LI>
        <LI><><strong>Hold (median / mean)</strong> — how long trades stay open.</></LI>
      </List>

      <H id="charts">The charts</H>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Rolling expectancy</strong> (last 20 trades) — is your edge improving or decaying?</></LI>
        <LI><><strong>Daily P&L</strong> — green/red bars per day.</></LI>
        <LI><><strong>Equity curve</strong> — cumulative ₹ P&L over time, now with a dashed{' '}
          <strong>buy-&-hold BTC</strong> line on the right axis (and a "vs BTC +X%" header) so you can see
          whether you actually beat just holding the market.</></LI>
        <LI><><strong>R-multiple distribution</strong> — histogram of every trade's R; a healthy trend style
          shows many small losses and a few large wins.</></LI>
        <LI><><strong>Hold-time histogram</strong> — distribution of trade durations.</></LI>
      </List>

      <H id="efficiency">Exit efficiency (Optimal ROI card)</H>
      <P>
        Answers "what target should I realistically aim for, and am I exiting well?" — from the favourable
        (MFE) and adverse (MAE) excursions of each trade:
      </P>
      <List dense sx={{ pl: 1 }}>
        <LI><><strong>Capture efficiency</strong> — the % of the peak move you actually banked (realized R ÷ MFE R).</></LI>
        <LI><><strong>Left on table</strong> — average R you gave back after the peak (avg MFE − avg realized).
          Big = you're exiting winners too early or too loosely.</></LI>
        <LI><><strong>Avg heat (MAE)</strong> — the typical adverse excursion a trade endured before its outcome —
          how much pain you sat through. Accrues on new trades (it's tracked live from now on).</></LI>
        <LI><><strong>Suggested target</strong> — the fixed R-target that would have maximised expectancy on your
          own MFE data.</></LI>
      </List>

      <H id="insights">Insights, breakdown &amp; trades explorer</H>
      <P>
        Below the KPIs, the <strong>Key findings</strong> card ranks plain-English "what's working / what's hurting"
        observations; the <strong>Breakdown explorer</strong> lets you click any dimension (symbol / side / exit /
        timeframe / strategy / tag) to filter the <strong>Trades explorer</strong> table underneath — so you can go
        from "shorts on X are bleeding" to the exact trades in one click. A daily <strong>P&L calendar heatmap</strong>{' '}
        shows your day-by-day rhythm.
      </P>

      <GuideCallout kind="info" title="R-multiple">
        "R" is profit/loss measured in units of the risk you took on entry (entry → stop distance). +2R means
        you made twice what you risked. Reporting in R makes trades of different sizes comparable.
      </GuideCallout>
      <GuideCallout kind="tip">
        Compare <strong>Auto</strong> vs <strong>Manual</strong> scope to see whether your automation or your
        discretionary trades are carrying the account — then double down on what works.
      </GuideCallout>
    </>
  ),
  searchIndex: [
    { heading: 'Performance Analytics', anchor: 'kpis', body: 'analytics performance net pnl win rate expectancy profit factor avg r max drawdown capture efficiency hold' },
    { heading: 'Scope and range', anchor: 'scope', body: 'analytics scope all auto manual edge ai date range 7d 30d 90d 1y validate picks real money' },
    { heading: 'Analytics charts', anchor: 'charts', body: 'rolling expectancy daily pnl equity curve buy hold btc benchmark r multiple distribution hold time histogram' },
    { heading: 'Exit efficiency', anchor: 'efficiency', body: 'capture efficiency left on table avg heat mae mfe optimal target exit discipline' },
    { heading: 'Insights breakdown trades explorer', anchor: 'insights', body: 'key findings insights breakdown explorer trades explorer calendar heatmap filter dimension' },
  ],
};

export default Section_analytics;
